import React from 'react';
import { Button, Select, Space, Typography, message } from 'antd';
import type { DeliveryGroupDraft, DeliveryGroupStopDraft } from './deliveryGroupTypes';
import {
  hydrateLoadingStopFromApplication,
  hydrateUnloadingStopFromApplication
} from './executionBindingHelpers';
import { RouteGroupStopCard, type RouteStopCardContext } from './RouteGroupStopCard';
import { formatGroupTitle, ROUTE_UX } from './routeUxLabels';
import {
  buildApplicationPickerSelectOptions,
  formatApplicationOccupiedRouteLabel,
  isApplicationAvailableForRoutePlanning,
  resolveApplicationActiveRouteId,
  type RoutePlanningApplicationRef
} from './routePlanningApplications';
import { nextStopPosition } from './editorStopOrdering';
import { isSequentialStopsEditorModeEnabled } from './sequentialStopsEditorFlags';

const { Text, Title } = Typography;

type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  client?: { name?: string | null } | null;
  consigneeId?: number | null;
  loadingAddress?: string | null;
  loadingContactPerson?: string | null;
  loadingPhone?: string | null;
  unloadingAddress?: string | null;
  unloadingContactPerson?: string | null;
  unloadingPhone?: string | null;
  consignee?: { name?: string | null; contactPerson?: string | null; phone?: string | null; address?: string | null } | null;
};

type Props = {
  group: DeliveryGroupDraft;
  /** All route groups — used to assign global draft.position on new stops. */
  allGroups?: DeliveryGroupDraft[];
  groupIndex: number;
  onChange: (next: DeliveryGroupDraft) => void;
  onRemove?: () => void;
  applications: ApplicationRef[];
  /** When editing a route, exclude its id from occupancy checks. */
  editingRouteId?: number;
  warehouses?: RouteStopCardContext['warehouses'];
  consignees?: RouteStopCardContext['consignees'];
  makeStopUid: () => string;
  inTransit?: boolean;
  allStops?: DeliveryGroupStopDraft[];
  resolveCityHint?: (address: string) => Promise<string | null>;
  normalizeCity?: (city: string | null | undefined) => string | null;
};

function assignPositionOnNewStop(
  draft: DeliveryGroupStopDraft,
  allGroups: DeliveryGroupDraft[] | undefined
): DeliveryGroupStopDraft {
  if (!isSequentialStopsEditorModeEnabled()) return draft;
  const groups = allGroups ?? [];
  return { ...draft, position: nextStopPosition(groups) };
}

export function RouteGroupCard({
  group,
  allGroups,
  groupIndex,
  onChange,
  onRemove,
  applications,
  editingRouteId,
  warehouses,
  consignees,
  makeStopUid,
  inTransit,
  allStops,
  resolveCityHint,
  normalizeCity
}: Props) {
  const app = group.applicationId ? applications.find((a) => a.id === group.applicationId) : undefined;
  const groupStops = [...group.loadingStops, ...group.unloadingStops];
  const groupHasCompletedStop = groupStops.some((s) => s.lockState === 'COMPLETED');
  const groupApplicationLocked = Boolean(inTransit && groupHasCompletedStop);

  const applicationSelectOptions = React.useMemo(
    () =>
      buildApplicationPickerSelectOptions({
        applications: applications as RoutePlanningApplicationRef[],
        excludeRouteId: editingRouteId,
        retainApplicationId: group.applicationId
      }),
    [applications, editingRouteId, group.applicationId]
  );

  const applyApplicationToGroup = (applicationId: number | null) => {
    if (applicationId != null) {
      const candidate = applications.find((a) => a.id === applicationId) as
        | RoutePlanningApplicationRef
        | undefined;
      if (
        candidate &&
        group.applicationId !== applicationId &&
        !isApplicationAvailableForRoutePlanning(candidate, editingRouteId)
      ) {
        const occupiedRouteId = resolveApplicationActiveRouteId(candidate, editingRouteId);
        message.warning(
          occupiedRouteId != null
            ? formatApplicationOccupiedRouteLabel(occupiedRouteId)
            : 'Заявка недоступна для планирования'
        );
        return;
      }
    }

    const selected = applicationId ? applications.find((a) => a.id === applicationId) : undefined;

    let loadingStops = group.loadingStops;
    if (selected) {
      if (loadingStops.length === 0) {
        loadingStops = [
          hydrateLoadingStopFromApplication(
            {
              uid: makeStopUid(),
              groupUid: group.uid,
              type: 'loading' as const,
              pointType: 'application',
              pointId: selected.id,
              address: null,
              contactPerson: null,
              phone: null,
              plannedAt: null,
              actualAt: null,
              notes: null,
              status: null
            },
            selected
          )
        ];
      } else {
        loadingStops = loadingStops.map((s, idx) =>
          idx === 0
            ? hydrateLoadingStopFromApplication(
                { ...s, pointType: 'application', pointId: selected.id },
                selected
              )
            : s
        );
      }
    }

    let unloadingStops = group.unloadingStops;
    if (selected) {
      if (unloadingStops.length === 0) {
        unloadingStops = [
          hydrateUnloadingStopFromApplication(
            {
              uid: makeStopUid(),
              groupUid: group.uid,
              type: 'unloading',
              pointType: 'application',
              pointId: selected.id,
              address: null,
              contactPerson: null,
              phone: null,
              plannedAt: null,
              actualAt: null,
              notes: null,
              status: null
            },
            selected
          )
        ];
      } else {
        unloadingStops = unloadingStops.map((s, idx) =>
          idx === 0 &&
          (s.pointType === 'application' || s.pointType === 'consignee' || s.pointType == null)
            ? hydrateUnloadingStopFromApplication(
                { ...s, pointType: 'application', pointId: selected.id },
                selected
              )
            : s
        );
      }
    }

    onChange({
      ...group,
      applicationId,
      loadingStops,
      unloadingStops
    });
  };

  const stopCtx: RouteStopCardContext = {
    warehouses,
    consignees,
    application: app ?? null,
    groupApplicationId: group.applicationId ?? null,
    applicationConsigneeId: app?.consigneeId ?? null,
    inTransit,
    allStops,
    resolveCityHint,
    normalizeCity
  };

  const title = formatGroupTitle({
    applicationNumber: app?.internalNumber ?? null,
    clientName: app?.client?.name ?? null,
    index: groupIndex
  });

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
        background: '#fafcff'
      }}
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        {onRemove ? (
          <Button type="link" danger disabled={groupHasCompletedStop} onClick={onRemove}>
            {ROUTE_UX.removeGroup}
          </Button>
        ) : null}
      </Space>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Text type="secondary">Заявка для этой группы маршрута</Text>
        <Select
          style={{ width: '100%', maxWidth: 480 }}
          placeholder={ROUTE_UX.selectApplication}
          showSearch
          optionFilterProp="label"
          disabled={groupApplicationLocked}
          value={group.applicationId ?? undefined}
          onChange={(val) => applyApplicationToGroup(val ?? null)}
          options={applicationSelectOptions}
        />
      </Space>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {ROUTE_UX.loadingSection}
      </Text>
      {group.loadingStops.length === 0 && group.applicationId ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Адрес погрузки подставлен из заявки. При необходимости добавьте дополнительную точку.
        </Text>
      ) : null}
      {group.loadingStops.map((s, idx) => (
        <RouteGroupStopCard
          key={s.uid}
          stop={s}
          stopType="loading"
          index={idx}
          ctx={stopCtx}
          onChange={(next) =>
            onChange({
              ...group,
              loadingStops: group.loadingStops.map((row) => (row.uid === s.uid ? next : row))
            })
          }
          onRemove={() =>
            onChange({ ...group, loadingStops: group.loadingStops.filter((row) => row.uid !== s.uid) })
          }
        />
      ))}
      <Button
        type="dashed"
        size="small"
        style={{ marginBottom: 16 }}
        onClick={() =>
          onChange({
            ...group,
            loadingStops: [
              ...group.loadingStops,
              assignPositionOnNewStop(
                {
                  uid: makeStopUid(),
                  groupUid: group.uid,
                  type: 'loading' as const,
                  pointType: group.applicationId ? 'application' : 'warehouse',
                  pointId: group.applicationId ?? null,
                  address: app?.loadingAddress ?? null,
                  contactPerson: app?.loadingContactPerson ?? null,
                  phone: app?.loadingPhone ?? null,
                  plannedAt: null,
                  actualAt: null,
                  notes: null,
                  status: null
                },
                allGroups
              )
            ]
          })
        }
      >
        + {ROUTE_UX.addLoadingStop}
      </Button>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {ROUTE_UX.unloadingSection}
      </Text>
      {group.unloadingStops.map((s, idx) => (
        <RouteGroupStopCard
          key={s.uid}
          stop={s}
          stopType="unloading"
          index={idx}
          ctx={stopCtx}
          onChange={(next) =>
            onChange({
              ...group,
              unloadingStops: group.unloadingStops.map((row) => (row.uid === s.uid ? next : row))
            })
          }
          onRemove={() =>
            onChange({ ...group, unloadingStops: group.unloadingStops.filter((row) => row.uid !== s.uid) })
          }
        />
      ))}
      <Button
        type="dashed"
        size="small"
        onClick={() =>
          onChange({
            ...group,
            unloadingStops: [
              ...group.unloadingStops,
              assignPositionOnNewStop(
                {
                  uid: makeStopUid(),
                  groupUid: group.uid,
                  type: 'unloading' as const,
                  pointType: 'application',
                  pointId: group.applicationId ?? app?.id ?? null,
                  address: app?.unloadingAddress ?? app?.consignee?.address ?? null,
                  contactPerson: app?.unloadingContactPerson ?? app?.consignee?.contactPerson ?? null,
                  phone: app?.unloadingPhone ?? app?.consignee?.phone ?? null,
                  plannedAt: null,
                  actualAt: null,
                notes: null,
                status: null
                },
                allGroups
              )
            ]
          })
        }
      >
        + {ROUTE_UX.addUnloadingStop}
      </Button>
    </div>
  );
}
